import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, MessageSquare, Phone, Send, Search, Filter, Clock, 
  User, Users, Briefcase, FileText, Plus, X, Paperclip,
  Check, AlertCircle, ChevronDown, Eye, Trash2, RefreshCw,
  Inbox, ArrowRight, Calendar, CheckCircle, XCircle, Loader2,
  ExternalLink, Copy, Shield, Lock, History, UserPlus, Video,
  PhoneCall, PhoneIncoming, PhoneOutgoing, PhoneMissed, VideoIcon
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { communicationAPI, messagingAPI } from '../../services/api';
import api from '../../services/api';
import VideoCallModal from '../common/VideoCallModal';

// Tab configuration
const COMM_TABS = [
  { id: 'email', label: 'Email Center', icon: Mail },
  { id: 'messages', label: 'Internal Messages', icon: MessageSquare },
  { id: 'quickreplies', label: 'Quick Replies', icon: FileText },
  { id: 'calls', label: 'Video & Voice Calls', icon: Video },
  { id: 'logs', label: 'Communication Log', icon: History },
];

const RECIPIENT_TYPES = [
  { id: 'customer', label: 'Customers', icon: User },
  { id: 'worker', label: 'Workers', icon: Briefcase },
  { id: 'applicant', label: 'Applicants', icon: Users },
];

const CommunicationCenterTab = () => {
  const [activeTab, setActiveTab] = useState('email');
  const [stats, setStats] = useState({ total_emails: 0, total_sms: 0, total_messages: 0, unread_messages: 0, today_emails: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await communicationAPI.getStats();
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 font-['Cormorant_Garamond']">
            <Inbox className="w-6 h-6 text-[var(--primary)]" />
            Communication Center
          </h2>
          <p className="text-[var(--text-muted)] mt-1">
            Manage all communications with customers, workers, and applicants
          </p>
        </div>
        
        {/* Quick Stats */}
        <div className="flex flex-wrap gap-3">
          <div className="px-4 py-2 bg-blue-500/10 rounded-lg text-center border border-blue-500/20">
            <p className="text-2xl font-bold text-blue-400">{stats.total_emails}</p>
            <p className="text-xs text-[var(--text-muted)]">Emails Sent</p>
          </div>
          <div className="px-4 py-2 bg-emerald-500/10 rounded-lg text-center border border-emerald-500/20">
            <p className="text-2xl font-bold text-emerald-400">{stats.today_emails || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">Today</p>
          </div>
          <div className="px-4 py-2 bg-purple-500/10 rounded-lg text-center border border-purple-500/20">
            <p className="text-2xl font-bold text-purple-400">{stats.total_messages}</p>
            <p className="text-xs text-[var(--text-muted)]">Messages</p>
          </div>
          {stats.unread_messages > 0 && (
            <div className="px-4 py-2 bg-red-500/10 rounded-lg text-center border border-red-500/20">
              <p className="text-2xl font-bold text-red-400">{stats.unread_messages}</p>
              <p className="text-xs text-[var(--text-muted)]">Unread</p>
            </div>
          )}
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-emerald-400">Secure Communications</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            All communications are logged and stored securely. Email replies go directly to nchelphub@outlook.com.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-[var(--border)] overflow-x-auto">
        {COMM_TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`comm-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-[var(--primary)] text-[var(--primary)]'
                  : 'border-transparent text-[var(--text-muted)] hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'messages' && stats.unread_messages > 0 && (
                <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {stats.unread_messages}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'email' && (
          <motion.div
            key="email"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <EmailCenterSection onEmailSent={fetchStats} />
          </motion.div>
        )}
        {activeTab === 'messages' && (
          <motion.div
            key="messages"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <InternalMessagesSection onMessageSent={fetchStats} />
          </motion.div>
        )}
        {activeTab === 'quickreplies' && (
          <motion.div
            key="quickreplies"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <QuickRepliesSection />
          </motion.div>
        )}
        {activeTab === 'calls' && (
          <motion.div
            key="calls"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <VideoCallsSection />
          </motion.div>
        )}
        {activeTab === 'logs' && (
          <motion.div
            key="logs"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <CommunicationLogSection />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============== EMAIL CENTER SECTION ==============
const EmailCenterSection = ({ onEmailSent }) => {
  const [showComposer, setShowComposer] = useState(false);
  const [recipientType, setRecipientType] = useState('customer');
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [templates, setTemplates] = useState({ custom_templates: [], saved_templates: {} });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [drafts, setDrafts] = useState([]);

  // Fetch recipients when type or search changes
  useEffect(() => {
    fetchRecipients();
  }, [recipientType, searchQuery]);

  useEffect(() => {
    fetchTemplates();
    fetchDrafts();
  }, []);

  const fetchRecipients = async () => {
    setLoadingRecipients(true);
    try {
      const res = await communicationAPI.getRecipients(recipientType, searchQuery);
      setRecipients(res.data.recipients || []);
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await communicationAPI.getEmailTemplates();
      setTemplates(res.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchDrafts = async () => {
    try {
      const res = await communicationAPI.getDrafts();
      setDrafts(res.data.drafts || []);
    } catch (error) {
      console.error('Failed to fetch drafts:', error);
    }
  };

  const handleSelectRecipient = (recipient) => {
    if (selectedRecipients.find(r => r.id === recipient.id)) {
      setSelectedRecipients(selectedRecipients.filter(r => r.id !== recipient.id));
    } else {
      setSelectedRecipients([...selectedRecipients, recipient]);
    }
  };

  const handleSelectAll = () => {
    if (selectedRecipients.length === recipients.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients([...recipients]);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setSubject(template.subject || '');
    setBodyHtml(template.body || template.body_html || '');
  };

  const handleSendEmail = async (saveAsDraft = false) => {
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!bodyHtml.trim()) {
      toast.error('Please enter email content');
      return;
    }

    setSending(true);
    try {
      const res = await communicationAPI.sendEmail({
        recipient_ids: selectedRecipients.map(r => r.id),
        recipient_type: recipientType,
        subject,
        body_html: bodyHtml,
        template_id: selectedTemplate?.id,
        save_as_draft: saveAsDraft
      });

      if (saveAsDraft) {
        toast.success('Draft saved');
        fetchDrafts();
      } else {
        toast.success(res.data.message);
        if (onEmailSent) onEmailSent();
      }

      // Reset form
      setSelectedRecipients([]);
      setSubject('');
      setBodyHtml('');
      setSelectedTemplate(null);
      setShowComposer(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      await communicationAPI.deleteDraft(draftId);
      toast.success('Draft deleted');
      fetchDrafts();
    } catch (error) {
      toast.error('Failed to delete draft');
    }
  };

  const handleLoadDraft = (draft) => {
    setSubject(draft.subject);
    setBodyHtml(draft.body_html);
    setShowComposer(true);
  };

  // Open email in Outlook Web
  const openInOutlook = () => {
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient first');
      return;
    }
    const emails = selectedRecipients.map(r => r.email).join(',');
    const mailtoUrl = `mailto:${emails}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyHtml.replace(/<[^>]+>/g, ''))}`;
    window.open(mailtoUrl, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          <Button data-testid="compose-email-btn" onClick={() => setShowComposer(true)} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Compose Email
          </Button>
        </div>
        
        {drafts.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
            <FileText className="w-4 h-4" />
            {drafts.length} draft(s)
          </div>
        )}
      </div>

      {/* Email Composer Modal */}
      <AnimatePresence>
        {showComposer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowComposer(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col border border-[var(--border)]"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-[var(--border)] flex items-center justify-between bg-gradient-to-r from-[var(--primary)]/10 to-transparent">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[var(--primary)]" />
                  Compose Email
                </h3>
                <Button variant="ghost" onClick={() => setShowComposer(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left: Recipient Selection */}
                  <div className="space-y-4">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Select Recipients
                    </h4>

                    {/* Recipient Type Tabs */}
                    <div className="flex gap-1 bg-[var(--background)] p-1 rounded-lg">
                      {RECIPIENT_TYPES.map(type => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.id}
                            data-testid={`recipient-type-${type.id}`}
                            onClick={() => { setRecipientType(type.id); setSelectedRecipients([]); }}
                            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-sm transition-all ${
                              recipientType === type.id
                                ? 'bg-[var(--primary)] text-white'
                                : 'text-[var(--text-muted)] hover:text-white'
                            }`}
                          >
                            <Icon className="w-3 h-3" />
                            {type.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                      <Input
                        data-testid="recipient-search-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by name or email..."
                        className="pl-10 bg-[var(--background)] text-white border-[var(--border)]"
                      />
                    </div>

                    {/* Select All */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[var(--text-muted)]">
                        {selectedRecipients.length} of {recipients.length} selected
                      </span>
                      <button data-testid="select-all-recipients" onClick={handleSelectAll} className="text-[var(--primary)] hover:underline">
                        {selectedRecipients.length === recipients.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    {/* Recipients List */}
                    <div className="max-h-[300px] overflow-y-auto space-y-1 border border-[var(--border)] rounded-lg p-2">
                      {loadingRecipients ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-[var(--primary)]" />
                        </div>
                      ) : recipients.length === 0 ? (
                        <p className="text-center py-4 text-[var(--text-muted)]">No recipients found</p>
                      ) : (
                        recipients.map(recipient => (
                          <button
                            key={recipient.id}
                            onClick={() => handleSelectRecipient(recipient)}
                            className={`w-full text-left p-2 rounded-lg transition-all flex items-center gap-3 ${
                              selectedRecipients.find(r => r.id === recipient.id)
                                ? 'bg-[var(--primary)]/20 border border-[var(--primary)]'
                                : 'hover:bg-[var(--background)] border border-transparent'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                              selectedRecipients.find(r => r.id === recipient.id)
                                ? 'bg-[var(--primary)] border-[var(--primary)]'
                                : 'border-[var(--border)]'
                            }`}>
                              {selectedRecipients.find(r => r.id === recipient.id) && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate text-white">{recipient.name}</p>
                              <p className="text-xs text-[var(--text-muted)] truncate">{recipient.email}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Right: Email Content */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Template Selection */}
                    <div>
                      <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">
                        Use Template (Optional)
                      </label>
                      <select
                        value={selectedTemplate?.id || ''}
                        onChange={(e) => {
                          const template = [...(templates.custom_templates || [])].find(t => t.id === e.target.value);
                          if (template) handleSelectTemplate(template);
                          else {
                            setSelectedTemplate(null);
                            setSubject('');
                            setBodyHtml('');
                          }
                        }}
                        className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg text-white"
                      >
                        <option value="">-- Select a template --</option>
                        {templates.custom_templates?.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">
                        Subject *
                      </label>
                      <Input
                        data-testid="email-subject-input"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Email subject..."
                        className="bg-[var(--background)] text-white border-[var(--border)]"
                      />
                    </div>

                    {/* Body */}
                    <div>
                      <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">
                        Message *
                      </label>
                      <Textarea
                        data-testid="email-body-input"
                        value={bodyHtml}
                        onChange={(e) => setBodyHtml(e.target.value)}
                        placeholder={`Write your email message here...

You can use variables like:
{customer_name} - Recipient's name
{business_name} - Your business name
{booking_reference} - Booking reference number`}
                        rows={10}
                        className="bg-[var(--background)] text-white border-[var(--border)] font-mono text-sm"
                      />
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-[var(--text-muted)]">
                          Tip: Use {"{ }"} variables to personalize each email
                        </p>
                        <button
                          onClick={openInOutlook}
                          className="text-xs text-[var(--primary)] hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open in Outlook
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-[var(--border)] flex items-center justify-between bg-[var(--background)]">
                <div className="flex items-center gap-2">
                  <Button data-testid="save-draft-btn" variant="outline" onClick={() => handleSendEmail(true)} disabled={sending}>
                    <FileText className="w-4 h-4 mr-2" />
                    Save Draft
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setShowComposer(false)}>
                    Cancel
                  </Button>
                  <Button data-testid="send-email-btn" onClick={() => handleSendEmail(false)} disabled={sending || selectedRecipients.length === 0}>
                    {sending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send to {selectedRecipients.length} Recipient{selectedRecipients.length !== 1 ? 's' : ''}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Drafts Section */}
      {drafts.length > 0 && (
        <div className="bg-[var(--background-paper)] rounded-xl border border-[var(--border)] p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-500" />
            Saved Drafts
          </h4>
          <div className="space-y-2">
            {drafts.map(draft => (
              <div
                key={draft.id}
                className="flex items-center justify-between p-3 bg-[var(--background)] rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate text-white">{draft.subject || '(No subject)'}</p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {draft.recipient_ids?.length || 0} recipient(s) • Updated {new Date(draft.updated_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleLoadDraft(draft)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteDraft(draft.id)} className="text-red-500 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Email Info */}
      <div className="bg-[var(--background-paper)] rounded-xl border border-[var(--border)] p-4">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <Mail className="w-4 h-4 text-blue-400" />
          Email Configuration
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="p-3 bg-[var(--background)] rounded-lg">
            <p className="text-[var(--text-muted)]">Emails are sent from:</p>
            <p className="font-medium text-white">NC Help Hub &lt;noreply@nchelphub.com&gt;</p>
          </div>
          <div className="p-3 bg-[var(--background)] rounded-lg">
            <p className="text-[var(--text-muted)]">Replies go to:</p>
            <p className="font-medium text-emerald-400">nchelphub@outlook.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== INTERNAL MESSAGES SECTION ==============
const InternalMessagesSection = ({ onMessageSent }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [initialMessage, setInitialMessage] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [startingConversation, setStartingConversation] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchQuickReplies();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchQuickReplies = async () => {
    try {
      const res = await communicationAPI.getQuickReplies();
      setQuickReplies(res.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch quick replies:', error);
    }
  };

  const handleInsertQuickReply = async (template) => {
    setNewMessage(template.content);
    setShowQuickReplies(false);
    // Track usage
    try {
      await communicationAPI.useQuickReply(template.id);
    } catch (error) {
      console.log('Failed to track quick reply usage');
    }
  };

  const fetchConversations = async () => {
    try {
      // Get both support and direct conversations
      const [supportRes, directRes] = await Promise.all([
        messagingAPI.getSupportConversations().catch(() => ({ data: [] })),
        messagingAPI.getDirectConversations().catch(() => ({ data: [] }))
      ]);
      
      const allConversations = [
        ...(supportRes.data || []),
        ...(directRes.data || [])
      ].sort((a, b) => new Date(b.last_message_time || b.updated_at || 0) - new Date(a.last_message_time || a.updated_at || 0));
      
      setConversations(allConversations);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await messagingAPI.getAllUsers();
      setUsers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    }
  };

  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    try {
      const res = await messagingAPI.getConversationMessagesOwner(conv.id);
      setMessages(res.data || []);
      // Mark as read
      await messagingAPI.markAsRead(conv.id).catch(() => {});
      fetchConversations(); // Refresh to update unread counts
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      await messagingAPI.sendMessageAsOwner(selectedConversation.id, newMessage);
      setNewMessage('');
      // Refresh messages
      const res = await messagingAPI.getConversationMessagesOwner(selectedConversation.id);
      setMessages(res.data || []);
      fetchConversations();
      if (onMessageSent) onMessageSent();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStartNewConversation = async () => {
    if (!selectedUser || !initialMessage.trim()) {
      toast.error('Please select a user and enter a message');
      return;
    }

    setStartingConversation(true);
    try {
      const res = await messagingAPI.startDirectConversation(
        selectedUser.id,
        selectedUser.role,
        initialMessage
      );
      
      toast.success('Conversation started!');
      setShowNewConversation(false);
      setSelectedUser(null);
      setInitialMessage('');
      setUserSearch('');
      fetchConversations();
      
      // Select the new conversation
      if (res.data?.conversation_id) {
        const newConv = { id: res.data.conversation_id, ...res.data };
        handleSelectConversation(newConv);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    } finally {
      setStartingConversation(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button 
          data-testid="new-conversation-btn"
          onClick={() => { setShowNewConversation(true); fetchUsers(); }}
          className="flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          New Conversation
        </Button>
        <Button data-testid="refresh-conversations-btn" variant="outline" onClick={fetchConversations}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* New Conversation Modal */}
      <AnimatePresence>
        {showNewConversation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowNewConversation(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-2xl w-full max-w-lg overflow-hidden border border-[var(--border)]"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[var(--primary)]" />
                  Start New Conversation
                </h3>
                <Button variant="ghost" onClick={() => setShowNewConversation(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="p-6 space-y-4">
                {/* User Search */}
                <div>
                  <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">
                    Select Recipient
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                    <Input
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search users..."
                      className="pl-10 bg-[var(--background)] text-white border-[var(--border)]"
                    />
                  </div>
                </div>

                {/* User List */}
                <div className="max-h-[200px] overflow-y-auto space-y-1 border border-[var(--border)] rounded-lg p-2">
                  {filteredUsers.length === 0 ? (
                    <p className="text-center py-4 text-[var(--text-muted)]">
                      {users.length === 0 ? 'Loading users...' : 'No users found'}
                    </p>
                  ) : (
                    filteredUsers.map(user => (
                      <button
                        key={user.id}
                        onClick={() => setSelectedUser(user)}
                        className={`w-full text-left p-3 rounded-lg transition-all flex items-center gap-3 ${
                          selectedUser?.id === user.id
                            ? 'bg-[var(--primary)]/20 border border-[var(--primary)]'
                            : 'hover:bg-[var(--background)] border border-transparent'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-[var(--primary)]/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-[var(--primary)]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-white">{user.full_name}</p>
                          <p className="text-xs text-[var(--text-muted)] truncate">
                            {user.email} • {user.role}
                          </p>
                        </div>
                        {selectedUser?.id === user.id && (
                          <Check className="w-5 h-5 text-[var(--primary)]" />
                        )}
                      </button>
                    ))
                  )}
                </div>

                {/* Initial Message */}
                <div>
                  <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">
                    Message
                  </label>
                  <Textarea
                    value={initialMessage}
                    onChange={(e) => setInitialMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={4}
                    className="bg-[var(--background)] text-white border-[var(--border)]"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-[var(--border)] flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewConversation(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleStartNewConversation} 
                  disabled={!selectedUser || !initialMessage.trim() || startingConversation}
                >
                  {startingConversation ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Start Conversation
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[600px]">
        {/* Conversations List */}
        <div className="bg-[var(--background-paper)] rounded-xl border border-[var(--border)] overflow-hidden flex flex-col">
          <div className="p-4 border-b border-[var(--border)]">
            <h4 className="font-semibold flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Conversations ({conversations.length})
            </h4>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)]">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No conversations yet</p>
                <p className="text-xs mt-1">Start a new conversation above</p>
              </div>
            ) : (
              conversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`w-full text-left p-4 border-b border-[var(--border)] transition-all ${
                    selectedConversation?.id === conv.id
                      ? 'bg-[var(--primary)]/10'
                      : 'hover:bg-[var(--background)]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-medium truncate text-white">
                      {Object.values(conv.participant_names || {}).filter(n => n !== 'Owner' && n !== 'NC Help Hub').join(', ') || conv.conversation_type || 'Conversation'}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="px-2 py-0.5 bg-[var(--primary)] text-white text-xs rounded-full">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--text-muted)] truncate mt-1">
                    {conv.last_message || 'No messages'}
                  </p>
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {conv.last_message_time ? new Date(conv.last_message_time).toLocaleDateString() : ''}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Messages View */}
        <div className="lg:col-span-2 bg-[var(--background-paper)] rounded-xl border border-[var(--border)] overflow-hidden flex flex-col">
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-[var(--border)] bg-gradient-to-r from-[var(--primary)]/10 to-transparent">
                <h4 className="font-semibold text-white">
                  {Object.values(selectedConversation.participant_names || {}).filter(n => n !== 'Owner' && n !== 'NC Help Hub').join(', ') || 'Conversation'}
                </h4>
                <p className="text-xs text-[var(--text-muted)]">
                  {selectedConversation.conversation_type || 'Direct Message'}
                </p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-[var(--text-muted)]">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No messages yet</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_role === 'owner' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] p-3 rounded-lg ${
                        msg.sender_role === 'owner'
                          ? 'bg-[var(--primary)] text-white'
                          : 'bg-[var(--background)] text-white border border-[var(--border)]'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${msg.sender_role === 'owner' ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>
                          {msg.sender_name} • {new Date(msg.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-[var(--border)] bg-[var(--background)]">
                {/* Quick Reply Dropdown */}
                {showQuickReplies && quickReplies.length > 0 && (
                  <div className="mb-2 p-2 bg-[var(--background-paper)] rounded-lg border border-[var(--border)] max-h-[200px] overflow-y-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-[var(--text-muted)]">Quick Replies</span>
                      <button onClick={() => setShowQuickReplies(false)} className="text-[var(--text-muted)] hover:text-white">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-1">
                      {quickReplies.slice(0, 8).map(qr => (
                        <button
                          key={qr.id}
                          onClick={() => handleInsertQuickReply(qr)}
                          className="w-full text-left p-2 rounded hover:bg-[var(--primary)]/10 transition-all"
                        >
                          <p className="text-sm font-medium text-white">{qr.name}</p>
                          <p className="text-xs text-[var(--text-muted)] truncate">{qr.content}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowQuickReplies(!showQuickReplies)}
                    className="flex-shrink-0"
                    title="Quick Replies"
                    data-testid="quick-reply-toggle-btn"
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-[var(--background-paper)] text-white border-[var(--border)]"
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  />
                  <Button onClick={handleSendMessage} disabled={sending || !newMessage.trim()}>
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[var(--text-muted)]">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Select a conversation to view messages</p>
                <p className="text-xs mt-1">or start a new conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== VIDEO CALLS SECTION ==============
const VideoCallsSection = () => {
  const [contacts, setContacts] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCallModal, setShowCallModal] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const websocketRef = useRef(null);

  useEffect(() => {
    fetchContacts();
    fetchCallHistory();
    initWebSocket();
    
    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
      }
    };
  }, []);

  const initWebSocket = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/api/ws?token=${token}`);
      
      ws.onopen = () => console.log('Call WebSocket connected');
      ws.onclose = () => console.log('Call WebSocket disconnected');
      ws.onerror = (err) => console.error('Call WebSocket error:', err);
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'call_offer') {
            // Incoming call - show modal
            setActiveCall({
              callId: data.call_id,
              callType: data.call_type,
              direction: 'incoming',
              otherParty: {
                id: data.caller_id,
                name: data.caller_name,
                role: data.caller_role
              },
              offer: data.offer
            });
            setShowCallModal(true);
          }
        } catch (err) {
          console.error('WebSocket message error:', err);
        }
      };
      
      websocketRef.current = ws;
    } catch (error) {
      console.error('WebSocket init error:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await api.get('/calls/contacts');
      setContacts(res.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const fetchCallHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get('/calls/history?limit=50');
      setCallHistory(res.data.calls || []);
    } catch (error) {
      console.error('Failed to fetch call history:', error);
    } finally {
      setLoading(false);
    }
  };

  const initiateCall = async (contact, callType = 'video') => {
    try {
      const res = await api.post('/calls/initiate', {
        recipient_id: contact.id,
        call_type: callType
      });
      
      setActiveCall({
        callId: res.data.call_id,
        callType: callType,
        direction: 'outgoing',
        otherParty: res.data.recipient
      });
      setShowCallModal(true);
      setSelectedContact(null);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start call');
    }
  };

  const handleCallEnd = (callData) => {
    setShowCallModal(false);
    setActiveCall(null);
    fetchCallHistory();
    
    if (callData?.duration > 0) {
      toast.success(`Call ended (${Math.floor(callData.duration / 60)}:${(callData.duration % 60).toString().padStart(2, '0')})`);
    }
  };

  const filteredContacts = contacts.filter(c => 
    c.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCallStatusIcon = (call) => {
    if (call.status === 'missed' || call.status === 'no_answer') {
      return <PhoneMissed className="w-4 h-4 text-red-400" />;
    }
    if (call.direction === 'incoming') {
      return <PhoneIncoming className="w-4 h-4 text-emerald-400" />;
    }
    return <PhoneOutgoing className="w-4 h-4 text-blue-400" />;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Call Modal */}
      {showCallModal && activeCall && (
        <VideoCallModal
          isOpen={showCallModal}
          onClose={() => { setShowCallModal(false); setActiveCall(null); }}
          callData={activeCall}
          currentUser={{ id: 'owner' }}
          websocket={websocketRef.current}
          onCallEnd={handleCallEnd}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contacts Panel */}
        <div className="bg-[var(--background-paper)] rounded-xl border border-[var(--border)] overflow-hidden">
          <div className="p-4 border-b border-[var(--border)]">
            <h4 className="font-semibold flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[var(--primary)]" />
              Contacts
            </h4>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search contacts..."
                className="pl-10 bg-[var(--background)] text-white border-[var(--border)]"
              />
            </div>
          </div>
          
          <div className="max-h-[500px] overflow-y-auto">
            {filteredContacts.length === 0 ? (
              <div className="p-8 text-center text-[var(--text-muted)]">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No contacts found</p>
              </div>
            ) : (
              filteredContacts.map(contact => (
                <div
                  key={contact.id}
                  className="p-4 border-b border-[var(--border)] hover:bg-[var(--background)] transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[var(--primary)]/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-[var(--primary)]" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{contact.full_name}</p>
                        <p className="text-xs text-[var(--text-muted)] capitalize">{contact.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => initiateCall(contact, 'voice')}
                        className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                        title="Voice Call"
                      >
                        <PhoneCall className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => initiateCall(contact, 'video')}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        title="Video Call"
                      >
                        <Video className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Call History Panel */}
        <div className="lg:col-span-2 bg-[var(--background-paper)] rounded-xl border border-[var(--border)] overflow-hidden">
          <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
            <h4 className="font-semibold flex items-center gap-2">
              <History className="w-4 h-4 text-[var(--primary)]" />
              Call History
            </h4>
            <Button variant="outline" size="sm" onClick={fetchCallHistory}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="max-h-[500px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
              </div>
            ) : callHistory.length === 0 ? (
              <div className="p-8 text-center text-[var(--text-muted)]">
                <Phone className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No call history yet</p>
                <p className="text-xs mt-1">Start a call from the contacts panel</p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--border)]">
                {callHistory.map(call => (
                  <div key={call.id} className="p-4 hover:bg-[var(--background)] transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          call.status === 'missed' || call.status === 'no_answer'
                            ? 'bg-red-500/10'
                            : call.call_type === 'video'
                              ? 'bg-blue-500/10'
                              : 'bg-emerald-500/10'
                        }`}>
                          {call.call_type === 'video' ? (
                            <Video className={`w-5 h-5 ${
                              call.status === 'missed' ? 'text-red-400' : 'text-blue-400'
                            }`} />
                          ) : (
                            <Phone className={`w-5 h-5 ${
                              call.status === 'missed' ? 'text-red-400' : 'text-emerald-400'
                            }`} />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white">
                              {call.other_party?.name || 'Unknown'}
                            </p>
                            {getCallStatusIcon(call)}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                            <span className="capitalize">{call.other_party?.role}</span>
                            <span>•</span>
                            <span>{new Date(call.started_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-medium ${
                          call.status === 'missed' || call.status === 'no_answer' || call.status === 'declined'
                            ? 'text-red-400'
                            : call.status === 'ended' || call.status === 'connected'
                              ? 'text-emerald-400'
                              : 'text-[var(--text-muted)]'
                        }`}>
                          {call.status === 'ended' && call.duration_seconds > 0
                            ? formatDuration(call.duration_seconds)
                            : call.status === 'missed' || call.status === 'no_answer'
                              ? 'Missed'
                              : call.status === 'declined'
                                ? 'Declined'
                                : call.status
                          }
                        </p>
                        <p className="text-xs text-[var(--text-muted)] capitalize">
                          {call.direction} • {call.call_type}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Call Info */}
      <div className="bg-[var(--background-paper)] rounded-xl border border-[var(--border)] p-4">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-emerald-400" />
          In-App Calling Features
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="p-3 bg-[var(--background)] rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Video className="w-4 h-4 text-blue-400" />
              <p className="font-medium text-white">Video Calls</p>
            </div>
            <p className="text-xs text-[var(--text-muted)]">HD video calls with customers and workers</p>
          </div>
          <div className="p-3 bg-[var(--background)] rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <PhoneCall className="w-4 h-4 text-emerald-400" />
              <p className="font-medium text-white">Voice Calls</p>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Clear audio calls without video</p>
          </div>
          <div className="p-3 bg-[var(--background)] rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <History className="w-4 h-4 text-purple-400" />
              <p className="font-medium text-white">Call History</p>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Complete log of all calls with duration</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== QUICK REPLIES SECTION ==============
const QUICK_REPLY_CATEGORIES = [
  { id: 'all', label: 'All', icon: FileText },
  { id: 'greeting', label: 'Greetings', icon: MessageSquare },
  { id: 'confirmation', label: 'Confirmations', icon: CheckCircle },
  { id: 'scheduling', label: 'Scheduling', icon: Calendar },
  { id: 'payment', label: 'Payment', icon: FileText },
  { id: 'support', label: 'Support', icon: AlertCircle },
  { id: 'general', label: 'General', icon: MessageSquare },
  { id: 'custom', label: 'Custom', icon: Plus },
];

const QuickRepliesSection = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: '', content: '', category: 'general', shortcut: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await communicationAPI.getQuickReplies();
      setTemplates(res.data.templates || []);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch quick replies:', error);
      toast.error('Failed to load quick replies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      toast.error('Please fill in name and content');
      return;
    }

    setSaving(true);
    try {
      await communicationAPI.createQuickReply({
        name: formData.name,
        content: formData.content,
        category: formData.category,
        shortcut: formData.shortcut || null
      });
      toast.success('Quick reply created!');
      setShowCreateModal(false);
      setFormData({ name: '', content: '', category: 'general', shortcut: '' });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quick reply');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      toast.error('Please fill in name and content');
      return;
    }

    setSaving(true);
    try {
      await communicationAPI.updateQuickReply(editingTemplate.id, {
        name: formData.name,
        content: formData.content,
        category: formData.category,
        shortcut: formData.shortcut || null
      });
      toast.success('Quick reply updated!');
      setEditingTemplate(null);
      setFormData({ name: '', content: '', category: 'general', shortcut: '' });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update quick reply');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this quick reply?')) return;

    try {
      await communicationAPI.deleteQuickReply(templateId);
      toast.success('Quick reply deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete quick reply');
    }
  };

  const handleCopyToClipboard = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      content: template.content,
      category: template.category,
      shortcut: template.shortcut || ''
    });
  };

  const filteredTemplates = activeCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === activeCategory);

  const getCategoryLabel = (categoryId) => {
    const cat = QUICK_REPLY_CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.label : categoryId;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-[var(--primary)]" />
            Quick Reply Templates
          </h3>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Save and reuse common message responses with one click
          </p>
        </div>
        <Button 
          data-testid="create-quick-reply-btn"
          onClick={() => { setShowCreateModal(true); setFormData({ name: '', content: '', category: 'general', shortcut: '' }); }}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Quick Reply
        </Button>
      </div>

      {/* Category Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {QUICK_REPLY_CATEGORIES.map(cat => {
          const Icon = cat.icon;
          const count = cat.id === 'all' ? templates.length : templates.filter(t => t.category === cat.id).length;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeCategory === cat.id
                  ? 'bg-[var(--primary)] text-white'
                  : 'bg-[var(--background)] text-[var(--text-muted)] hover:bg-[var(--background-subtle)]'
              }`}
            >
              <Icon className="w-4 h-4" />
              {cat.label}
              {count > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                  activeCategory === cat.id ? 'bg-white/20' : 'bg-[var(--primary)]/20 text-[var(--primary)]'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-12 bg-[var(--background-paper)] rounded-xl border border-[var(--border)]">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50 text-[var(--text-muted)]" />
          <p className="text-[var(--text-muted)]">No quick replies in this category</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => { setShowCreateModal(true); setFormData({ name: '', content: '', category: activeCategory === 'all' ? 'general' : activeCategory, shortcut: '' }); }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create One
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => (
            <div
              key={template.id}
              className="bg-[var(--background-paper)] rounded-xl border border-[var(--border)] p-4 hover:border-[var(--primary)]/50 transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-white">{template.name}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--primary)]/10 text-[var(--primary)]">
                      {getCategoryLabel(template.category)}
                    </span>
                    {template.shortcut && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--background)] text-[var(--text-muted)] font-mono">
                        {template.shortcut}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleCopyToClipboard(template.content)}
                    title="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => openEditModal(template)}
                    title="Edit"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDelete(template.id)}
                    className="text-red-500 hover:text-red-400"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <p className="text-sm text-[var(--text-muted)] line-clamp-3">{template.content}</p>
              
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--border)]">
                <span className="text-xs text-[var(--text-muted)]">
                  Used {template.use_count || 0} times
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCopyToClipboard(template.content)}
                  className="text-xs"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {(showCreateModal || editingTemplate) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => { setShowCreateModal(false); setEditingTemplate(null); }}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-2xl w-full max-w-lg overflow-hidden border border-[var(--border)]"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[var(--primary)]" />
                  {editingTemplate ? 'Edit Quick Reply' : 'Create Quick Reply'}
                </h3>
                <Button variant="ghost" onClick={() => { setShowCreateModal(false); setEditingTemplate(null); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">Name *</label>
                  <Input
                    data-testid="quick-reply-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Thank You"
                    className="bg-[var(--background)] text-white border-[var(--border)]"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">Content *</label>
                  <Textarea
                    data-testid="quick-reply-content-input"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Type your quick reply message..."
                    rows={5}
                    className="bg-[var(--background)] text-white border-[var(--border)]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg text-white"
                    >
                      <option value="greeting">Greetings</option>
                      <option value="confirmation">Confirmations</option>
                      <option value="scheduling">Scheduling</option>
                      <option value="payment">Payment</option>
                      <option value="support">Support</option>
                      <option value="general">General</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">
                      Shortcut (Optional)
                    </label>
                    <Input
                      value={formData.shortcut}
                      onChange={(e) => setFormData({ ...formData, shortcut: e.target.value })}
                      placeholder="e.g., /thanks"
                      className="bg-[var(--background)] text-white border-[var(--border)] font-mono"
                    />
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      Type this in chat to insert the reply
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-[var(--border)] flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowCreateModal(false); setEditingTemplate(null); }}>
                  Cancel
                </Button>
                <Button 
                  data-testid="save-quick-reply-btn"
                  onClick={editingTemplate ? handleUpdate : handleCreate} 
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  {editingTemplate ? 'Update' : 'Create'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Usage Tips */}
      <div className="bg-[var(--background-paper)] rounded-xl border border-[var(--border)] p-4">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-blue-400" />
          How to Use Quick Replies
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="p-3 bg-[var(--background)] rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Copy className="w-4 h-4 text-emerald-400" />
              <p className="font-medium text-white">Copy & Paste</p>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Click the Copy button on any template to copy it to your clipboard</p>
          </div>
          <div className="p-3 bg-[var(--background)] rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="w-4 h-4 text-blue-400" />
              <p className="font-medium text-white">Shortcuts</p>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Use shortcuts like /thanks in chat to quickly insert templates</p>
          </div>
          <div className="p-3 bg-[var(--background)] rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Plus className="w-4 h-4 text-purple-400" />
              <p className="font-medium text-white">Create Custom</p>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Add your own frequently used responses for quick access</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== COMMUNICATION LOG SECTION ==============
const CommunicationLogSection = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', search: '' });
  const [total, setTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await communicationAPI.getLogs({
        communication_type: filter.type || undefined,
        search: filter.search || undefined,
        limit: 100
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'read':
        return <Eye className="w-4 h-4 text-blue-500" />;
      case 'failed':
      case 'bounced':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email':
        return <Mail className="w-4 h-4 text-blue-400" />;
      case 'sms':
        return <Phone className="w-4 h-4 text-emerald-400" />;
      case 'internal_message':
        return <MessageSquare className="w-4 h-4 text-purple-400" />;
      default:
        return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'email':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'sms':
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'internal_message':
        return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <Input
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            placeholder="Search by recipient, subject..."
            className="pl-10 bg-[var(--background)] text-white border-[var(--border)]"
          />
        </div>
        <select
          value={filter.type}
          onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          className="px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg text-white"
        >
          <option value="">All Types</option>
          <option value="email">Email</option>
          <option value="sms">SMS</option>
          <option value="internal_message">Internal Message</option>
        </select>
        <Button variant="outline" onClick={fetchLogs}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats */}
      <p className="text-sm text-[var(--text-muted)]">
        Showing {logs.length} of {total} communications
      </p>

      {/* Logs Table */}
      <div className="bg-[var(--background-paper)] rounded-xl border border-[var(--border)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No communication logs found</p>
            <p className="text-xs mt-1">Send an email or message to start logging</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--background)]">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-[var(--text-muted)]">Type</th>
                  <th className="text-left p-4 text-sm font-medium text-[var(--text-muted)]">Recipient</th>
                  <th className="text-left p-4 text-sm font-medium text-[var(--text-muted)]">Subject/Preview</th>
                  <th className="text-left p-4 text-sm font-medium text-[var(--text-muted)]">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-[var(--text-muted)]">Date</th>
                  <th className="text-left p-4 text-sm font-medium text-[var(--text-muted)]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className="border-t border-[var(--border)] hover:bg-[var(--background)]">
                    <td className="p-4">
                      <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full border ${getTypeBadgeClass(log.communication_type)}`}>
                        {getTypeIcon(log.communication_type)}
                        <span className="capitalize text-xs">{log.communication_type?.replace('_', ' ')}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-white">{log.recipient_name || 'Unknown'}</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {log.recipient_email || log.recipient_phone || ''}
                      </p>
                    </td>
                    <td className="p-4 max-w-[300px]">
                      <p className="font-medium truncate text-white">{log.subject || '(No subject)'}</p>
                      <p className="text-xs text-[var(--text-muted)] truncate">
                        {log.content_preview}
                      </p>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(log.status)}
                        <span className="capitalize text-white text-sm">{log.status}</span>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-[var(--text-muted)]">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="p-4">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedLog(log)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Log Detail Modal */}
      <AnimatePresence>
        {selectedLog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedLog(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-[var(--border)]"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  {getTypeIcon(selectedLog.communication_type)}
                  Communication Details
                </h3>
                <Button variant="ghost" onClick={() => setSelectedLog(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">Type</p>
                    <p className="font-medium text-white capitalize">{selectedLog.communication_type?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">Status</p>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(selectedLog.status)}
                      <span className="capitalize text-white">{selectedLog.status}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">Recipient</p>
                    <p className="font-medium text-white">{selectedLog.recipient_name}</p>
                    <p className="text-xs text-[var(--text-muted)]">{selectedLog.recipient_email || selectedLog.recipient_phone}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">Sent At</p>
                    <p className="font-medium text-white">{new Date(selectedLog.created_at).toLocaleString()}</p>
                  </div>
                </div>
                {selectedLog.subject && (
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">Subject</p>
                    <p className="font-medium text-white">{selectedLog.subject}</p>
                  </div>
                )}
                {selectedLog.content_html && (
                  <div>
                    <p className="text-xs text-[var(--text-muted)] mb-2">Content</p>
                    <div className="bg-[var(--background)] p-4 rounded-lg text-sm text-white whitespace-pre-wrap border border-[var(--border)]">
                      {selectedLog.content_html.replace(/<[^>]+>/g, '')}
                    </div>
                  </div>
                )}
                {selectedLog.reference_id && (
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">Reference</p>
                    <p className="font-medium text-white">{selectedLog.reference_type}: {selectedLog.reference_id}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommunicationCenterTab;
